import React, { useState } from 'react';
import { Icons } from '../../constants';
import { NoteType, ColorTheme } from '../../types';
import { Template, TEMPLATE_CATEGORIES } from '../../utils/templates';

interface TemplateWizardProps {
    isOpen: boolean;
    onClose: () => void;
    onCreateBoardFromTemplate: (template: Template) => void;
}

interface NoteConfig {
    type: NoteType;
    count: number;
    title: string;
}

export const TemplateWizard: React.FC<TemplateWizardProps> = ({
    isOpen,
    onClose,
    onCreateBoardFromTemplate,
}) => {
    const [step, setStep] = useState(1);
    const [name, setName] = useState('');
    const [description, setDescription] = useState('');
    const [category, setCategory] = useState<Template['category']>('productivity');
    const [themeColor, setThemeColor] = useState<ColorTheme>('blue');
    const [noteConfigs, setNoteConfigs] = useState<NoteConfig[]>([
        { type: NoteType.TEXT, count: 1, title: 'Main Note' },
    ]);

    if (!isOpen) return null;

    const addNoteConfig = () => {
        setNoteConfigs([...noteConfigs, { type: NoteType.STICKY, count: 1, title: 'New Note' }]);
    };

    const updateNoteConfig = (index: number, updates: Partial<NoteConfig>) => {
        const newConfigs = [...noteConfigs];
        newConfigs[index] = { ...newConfigs[index], ...updates };
        setNoteConfigs(newConfigs);
    };

    const removeNoteConfig = (index: number) => {
        setNoteConfigs(noteConfigs.filter((_, i) => i !== index));
    };

    const handleFinalize = () => {
        type TemplateElement = Template['elements'][0];
        const elements: TemplateElement[] = [];
        let currentX = 0;
        let currentY = 0;
        const spacing = 320;
        const rowWidth = 3;

        noteConfigs.forEach((config, configIdx) => {
            for (let i = 0; i < config.count; i++) {
                const x = (elements.length % rowWidth) * spacing;
                const y = Math.floor(elements.length / rowWidth) * spacing;

                elements.push({
                    type: config.type,
                    x,
                    y,
                    width: 300,
                    height: 250,
                    title: config.count > 1 ? `${config.title} ${i + 1}` : config.title,
                    colorTheme: themeColor,
                });
            }
        });

        const newTemplate: Template = {
            id: `custom-${Date.now()}`,
            name,
            description,
            category,
            iconName: 'Sparkles',
            elements,
        };

        onCreateBoardFromTemplate(newTemplate);
        onClose();
        // Reset state
        setStep(1);
        setName('');
        setDescription('');
        setNoteConfigs([{ type: NoteType.TEXT, count: 1, title: 'Main Note' }]);
    };

    return (
        <div className="fixed inset-0 z-[110] flex items-center justify-center p-4 bg-black/40 dark:bg-black/80 backdrop-blur-md animate-in fade-in duration-300">
            <div className="bg-white dark:bg-[#121212] rounded-[2.5rem] shadow-2xl w-full max-w-2xl overflow-hidden border border-slate-100 dark:border-slate-800 animate-in zoom-in-95 duration-300">
                {/* Header */}
                <div className="px-10 pt-10 pb-6 flex justify-between items-center border-b border-slate-50 dark:border-slate-800">
                    <div>
                        <h2 className="text-3xl font-black tracking-tight dark:text-white">Template Wizard</h2>
                        <p className="text-slate-500 dark:text-slate-400 text-sm font-medium mt-1">Step {step} of 3: {step === 1 ? 'Basics' : step === 2 ? 'Configure Notes' : 'Review'}</p>
                    </div>
                    <button onClick={onClose} className="p-2 rounded-full hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors">
                        <Icons.X className="w-6 h-6 text-slate-400" />
                    </button>
                </div>

                {/* Content */}
                <div className="p-10 max-h-[60vh] overflow-y-auto thin-scrollbar">
                    {step === 1 && (
                        <div className="space-y-6 animate-in slide-in-from-right-4 duration-300">
                            <div>
                                <label className="block text-xs font-black uppercase tracking-widest text-slate-400 mb-2 ml-1">Template Name</label>
                                <input
                                    type="text"
                                    value={name}
                                    onChange={(e) => setName(e.target.value)}
                                    placeholder="e.g., Weekly Sprint Plan"
                                    className="w-full bg-slate-50 dark:bg-[#1a1a1a] border-2 border-slate-100 dark:border-slate-800 rounded-2xl px-5 py-4 font-bold dark:text-white focus:outline-none focus:border-blue-500 transition-all shadow-sm"
                                />
                            </div>
                            <div>
                                <label className="block text-xs font-black uppercase tracking-widest text-slate-400 mb-2 ml-1">Description</label>
                                <textarea
                                    value={description}
                                    onChange={(e) => setDescription(e.target.value)}
                                    placeholder="What is this template for?"
                                    rows={3}
                                    className="w-full bg-slate-50 dark:bg-[#1a1a1a] border-2 border-slate-100 dark:border-slate-800 rounded-2xl px-5 py-4 font-medium dark:text-white focus:outline-none focus:border-blue-500 transition-all resize-none shadow-sm"
                                />
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-xs font-black uppercase tracking-widest text-slate-400 mb-2 ml-1">Category</label>
                                    <select
                                        value={category}
                                        onChange={(e) => setCategory(e.target.value as Template['category'])}
                                        className="w-full bg-slate-50 dark:bg-[#1a1a1a] border-2 border-slate-100 dark:border-slate-800 rounded-2xl px-5 py-4 font-bold dark:text-white focus:outline-none focus:border-blue-500 transition-all appearance-none"
                                    >
                                        {TEMPLATE_CATEGORIES.map(cat => (
                                            <option key={cat.id} value={cat.id}>{cat.name}</option>
                                        ))}
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-xs font-black uppercase tracking-widest text-slate-400 mb-2 ml-1">Theme Color</label>
                                    <select
                                        value={themeColor}
                                        onChange={(e) => setThemeColor(e.target.value as ColorTheme)}
                                        className="w-full bg-slate-50 dark:bg-[#1a1a1a] border-2 border-slate-100 dark:border-slate-800 rounded-2xl px-5 py-4 font-bold dark:text-white focus:outline-none focus:border-blue-500 transition-all appearance-none"
                                    >
                                        <option value="blue">Blue</option>
                                        <option value="green">Green</option>
                                        <option value="orange">Orange</option>
                                        <option value="purple">Purple</option>
                                        <option value="teal">Teal</option>
                                        <option value="dark">Dark</option>
                                    </select>
                                </div>
                            </div>
                        </div>
                    )}

                    {step === 2 && (
                        <div className="space-y-4 animate-in slide-in-from-right-4 duration-300">
                            <div className="flex justify-between items-center mb-2">
                                <label className="text-xs font-black uppercase tracking-widest text-slate-400 ml-1">Board Composition</label>
                                <button
                                    onClick={addNoteConfig}
                                    className="flex items-center gap-2 text-xs font-bold text-blue-500 hover:text-blue-600 transition-colors"
                                >
                                    <Icons.Plus className="w-4 h-4" /> Add Note Type
                                </button>
                            </div>

                            {noteConfigs.map((config, idx) => (
                                <div key={idx} className="bg-slate-50 dark:bg-[#1a1a1a] p-4 rounded-2xl border border-slate-100 dark:border-slate-800 flex items-center gap-4 animate-in fade-in slide-in-from-bottom-2 duration-200">
                                    <div className="flex-1">
                                        <input
                                            type="text"
                                            value={config.title}
                                            onChange={(e) => updateNoteConfig(idx, { title: e.target.value })}
                                            placeholder="Note Title"
                                            className="w-full bg-transparent font-bold dark:text-white focus:outline-none border-b border-transparent focus:border-blue-500/30 mb-2 px-1"
                                        />
                                        <select
                                            value={config.type}
                                            onChange={(e) => updateNoteConfig(idx, { type: e.target.value as NoteType })}
                                            className="text-xs font-bold text-slate-500 bg-transparent focus:outline-none"
                                        >
                                            {Object.values(NoteType).map(type => (
                                                <option key={type} value={type}>{type}</option>
                                            ))}
                                        </select>
                                    </div>
                                    <div className="flex items-center gap-3 bg-white dark:bg-[#121212] px-3 py-2 rounded-xl border border-slate-100 dark:border-slate-800 shadow-sm">
                                        <button
                                            onClick={() => updateNoteConfig(idx, { count: Math.max(1, config.count - 1) })}
                                            className="p-1 hover:bg-slate-50 dark:hover:bg-slate-800 rounded-md transition-colors"
                                        >
                                            <Icons.Minus className="w-4 h-4 text-slate-400" />
                                        </button>
                                        <span className="font-black text-sm min-w-[20px] text-center">{config.count}</span>
                                        <button
                                            onClick={() => updateNoteConfig(idx, { count: Math.min(10, config.count + 1) })}
                                            className="p-1 hover:bg-slate-50 dark:hover:bg-slate-800 rounded-md transition-colors"
                                        >
                                            <Icons.Plus className="w-4 h-4 text-slate-400" />
                                        </button>
                                    </div>
                                    <button
                                        onClick={() => removeNoteConfig(idx)}
                                        className="p-2 text-slate-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/10 rounded-xl transition-all"
                                    >
                                        <Icons.Trash className="w-5 h-5" />
                                    </button>
                                </div>
                            ))}

                            {noteConfigs.length === 0 && (
                                <div className="text-center py-10 opacity-50">
                                    <p className="font-bold text-slate-500">No notes added. Click "Add Note Type" above.</p>
                                </div>
                            )}
                        </div>
                    )}

                    {step === 3 && (
                        <div className="space-y-6 animate-in slide-in-from-right-4 duration-300">
                            <div className="bg-blue-50 dark:bg-blue-900/10 p-8 rounded-[2rem] border border-blue-100 dark:border-blue-900/30">
                                <div className="flex items-center gap-4 mb-4">
                                    <div className="w-12 h-12 rounded-2xl bg-white dark:bg-[#121212] flex items-center justify-center text-blue-500 shadow-sm">
                                        <Icons.Sparkles className="w-6 h-6" />
                                    </div>
                                    <div>
                                        <h4 className="font-black text-xl text-blue-900 dark:text-blue-100">{name || 'Untitled Template'}</h4>
                                        <p className="text-blue-700/60 dark:text-blue-300/60 text-sm font-bold uppercase tracking-widest">{category}</p>
                                    </div>
                                </div>
                                <p className="text-blue-800/70 dark:text-blue-200/70 font-medium">{description || 'No description provided.'}</p>
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                {noteConfigs.map((config, idx) => (
                                    <div key={idx} className="flex justify-between items-center px-5 py-4 bg-slate-50 dark:bg-[#1a1a1a] rounded-2xl border border-slate-100 dark:border-slate-800">
                                        <span className="font-bold text-slate-700 dark:text-slate-200">{config.title}</span>
                                        <span className="px-3 py-1 bg-white dark:bg-[#121212] rounded-lg text-xs font-black shadow-sm border border-slate-100 dark:border-slate-800">x{config.count}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="px-10 py-8 bg-slate-50/50 dark:bg-white/5 border-t border-slate-100 dark:border-slate-800 flex justify-between items-center">
                    <button
                        onClick={() => step > 1 && setStep(step - 1)}
                        className={`px-6 py-3 rounded-2xl font-black text-xs uppercase tracking-widest transition-all ${step === 1 ? 'opacity-0 pointer-events-none' : 'text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800'}`}
                    >
                        Back
                    </button>

                    {step < 3 ? (
                        <button
                            onClick={() => (step === 1 && !name.trim()) ? alert('Please enter a name') : setStep(step + 1)}
                            className="px-8 py-3 bg-slate-900 dark:bg-white text-white dark:text-black rounded-2xl font-black text-xs uppercase tracking-widest hover:scale-105 transition-all shadow-xl active:scale-95 flex items-center gap-2 disabled:opacity-50"
                        >
                            Continue <Icons.ArrowRight className="w-4 h-4" />
                        </button>
                    ) : (
                        <button
                            onClick={handleFinalize}
                            className="px-8 py-4 bg-blue-600 text-white rounded-2xl font-black text-xs uppercase tracking-widest hover:scale-105 transition-all shadow-xl shadow-blue-500/20 active:scale-95 flex items-center gap-2"
                        >
                            Create Template & Board <Icons.Check className="w-4 h-4" />
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
};
